import { Controller } from 'stimulus';

// Results Summary card (tasks index): hover-trace + multi-pin sample
// comparison, expansion accordion, pinned-comparison tray, comments toggle.
// Pin colors are the colorblind-validated 4-set; a 5th pin is refused.
const PIN_HUES = ['#2b6cb0', '#b83280', '#0987a0', '#975a16'];
const PIN_LIMIT = 4;
const LOW_N = 3;

export default class extends Controller {
  static targets = ['tray', 'pinHint', 'body', 'collapseLabel', 'commentsList', 'commentsToggle'];

  initialize() {
    this.pins = []; // { si, hue }
  }

  // ----- collapse the whole card -----
  toggleCollapse(event) {
    const hidden = this.bodyTarget.classList.toggle('hidden');
    this.collapseLabelTarget.textContent = hidden ? 'Expand' : 'Collapse';
    this.unfocusAfterClick(event);
  }

  // Mouse clicks shouldn't leave a lingering focus ring on the button;
  // keyboard activation (event.detail === 0) keeps focus where it was.
  unfocusAfterClick(event) {
    if (event.detail > 0) { event.currentTarget.blur(); }
  }

  // ----- field expansion (accordion: one open at a time) -----
  toggleExpand(event) {
    this.unfocusAfterClick(event);
    const id = event.currentTarget.getAttribute('data-xp');
    const panel = this.element.querySelector(`#${id}`);
    if (!panel) { return; }
    const willOpen = panel.classList.contains('hidden');
    this.element.querySelectorAll('.rs-xp').forEach((p) => p.classList.add('hidden'));
    this.element.querySelectorAll('.rs-frow[data-xp]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (willOpen) {
      panel.classList.remove('hidden');
      this.element.querySelectorAll(`[data-xp="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    }
  }

  toggleComments(event) {
    this.unfocusAfterClick(event);
    const hidden = this.commentsListTarget.classList.toggle('hidden');
    this.commentsToggleTarget.textContent = this.commentsToggleTarget.textContent.replace(hidden ? '▾' : '▸', hidden ? '▸' : '▾');
  }

  // ----- hover trace -----
  trace(event) {
    const el = event.target.closest('[data-si]');
    if (el) { this.applyTrace(parseInt(el.getAttribute('data-si'), 10)); }
  }

  untrace(event) {
    const el = event.target.closest('[data-si]');
    if (el) { this.applyTrace(null); }
  }

  applyTrace(hoverSi) {
    const pinned = new Set(this.pins.map((p) => p.si));
    const active = hoverSi !== null || pinned.size > 0;
    this.element.classList.toggle('rs-tracing', active);
    this.element.querySelectorAll('.rs-dot, .rs-schip').forEach((el) => {
      const si = parseInt(el.getAttribute('data-si'), 10);
      el.classList.toggle('rs-on', active && (pinned.has(si) || si === hoverSi));
    });
  }

  // ----- multi-pin (click sticks; each pin gets its own color) -----
  togglePin(event) {
    this.unfocusAfterClick(event);
    const si = parseInt(event.currentTarget.getAttribute('data-si'), 10);
    const at = this.pins.findIndex((p) => p.si === si);
    this.pinHintTarget.classList.add('hidden');
    if (at >= 0) {
      this.pins.splice(at, 1); // unpin frees its color
    } else if (this.pins.length >= PIN_LIMIT) {
      this.pinHintTarget.classList.remove('hidden');
      return;
    } else {
      const used = new Set(this.pins.map((p) => p.hue));
      this.pins.push({ si, hue: [0, 1, 2, 3].find((h) => !used.has(h)) });
    }
    this.applyPins();
    this.applyTrace(null);
    this.renderTray();
  }

  applyPins() {
    const bySi = new Map(this.pins.map((p) => [p.si, p.hue]));
    this.element.querySelectorAll('.rs-dot, .rs-schip').forEach((el) => {
      const si = parseInt(el.getAttribute('data-si'), 10);
      for (let h = 0; h < PIN_LIMIT; h += 1) { el.classList.remove(`rs-pin-${h}`); }
      if (bySi.has(si)) { el.classList.add(`rs-pin-${bySi.get(si)}`); }
      if (el.classList.contains('rs-schip')) { el.setAttribute('aria-pressed', bySi.has(si)); }
    });
  }

  // ----- pinned-comparison tray (2+ pins) -----
  renderTray() {
    if (this.pins.length < 2) {
      this.trayTarget.classList.add('hidden');
      this.trayTarget.innerHTML = '';
      return;
    }
    const names = {};
    this.element.querySelectorAll('.rs-schip').forEach((c) => { names[c.getAttribute('data-si')] = c.getAttribute('data-name'); });
    let html = '<table><thead><tr><th>Pinned comparison</th>';
    html += this.pins.map((p) => `<th><span class="rs-swatch" style="background:${PIN_HUES[p.hue]};"></span>${this.escape(names[p.si] || '')}</th>`).join('');
    html += '</tr></thead><tbody>';
    this.element.querySelectorAll('[data-tray-row]').forEach((row) => {
      const cells = JSON.parse(row.getAttribute('data-tray-cells') || '[]');
      html += `<tr><td class="rs-tray-field">${this.escape(row.getAttribute('data-tray-label') || '')}</td>`;
      this.pins.forEach((p) => { html += this.trayCell(cells[p.si]); });
      html += '</tr>';
    });
    html += '</tbody></table>';
    this.trayTarget.innerHTML = html;
    this.trayTarget.classList.remove('hidden');
  }

  trayCell(cell) {
    if (cell === null || cell === undefined) { return '<td class="rs-tray-na">—</td>'; }
    if (cell === 'NT') { return '<td class="rs-tray-na">not tested</td>'; }
    if (Array.isArray(cell) && cell.length >= 4) {
      const [value, fail, out, n] = cell;
      return `<td><span class="${fail ? 'rs-fail-text' : ''}">${this.escape(value)}</span>` +
        `${out ? ' <span class="rs-out-mark">◆</span>' : ''}` +
        `<span class="rs-tray-n${n < LOW_N ? ' rs-low-n' : ''}"> · N=${n}</span></td>`;
    }
    return `<td>${this.escape(cell[0] || '')}</td>`; // dropdown
  }

  escape(value) {
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
